// @flow
import React, { Fragment } from 'react';
import type { RouterHistory } from 'react-router-dom';
import { parse } from 'qs';
import { formatDate } from 'Utils';
import { Container, Row, Col } from 'Components/Grid';
import DefaultPage from 'Containers/DefaultPage';
import LoadingSpinner from 'Components/LoadingSpinner';
import Typography from 'Components/Typography';
import Dropdown from 'Components/Dropdown';
import AccessoriesEntry from './entry';
import DealerModel from 'Stores/DealerModel';
import AccessoryModel from 'Stores/AccessoryModel';
import MainStore from 'Stores/MainStore';
import styles from './styles.scss';
import axios from 'axios';
import { withUrlParams } from 'Utils/withUrlParams';
import { withRouter } from 'react-router-dom';

type Props = {
  carlines: Array<Object>,
  dealer: DealerModel,
  mainStore: MainStore,
  history: RouterHistory,
  deeplinkId?: number,
};

type State = {
  currentModel: string,
  currentYear: string,
  currentCategory: string,
  models: Array<Object>,
  years: Array<string>,
  categories: Array<Object>,
  parts: Array<Object>,
  loading: boolean,
};

type CarlineEntry = {
  id: string,
  name: string,
  years: Array<string>,
};

class AccessoriesList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const models = this.getGroupedCarlines(props.carlines);

    // init dropdowns
    const query = parse(window.location.search.replace('?', ''));
    const initialModelKey = query.model || (models.length && models[0].id) || '';
    const initialYearKey = query.year || '';
    const initialCategoryKey = query.category || '';
    const selectedCarline = models.find((model) => model.id === initialModelKey);

    this.state = {
      models,
      currentModel: initialModelKey,
      currentYear: initialYearKey,
      currentCategory: initialCategoryKey,
      years: selectedCarline ? selectedCarline.years : [],
      categories: [],
      parts: [],
      loading: false,
    };
  }

  componentDidMount() {
    const { currentModel, currentYear } = this.state;

    if (currentModel && currentYear) {
      this.loadCategories();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props &&
      this.props.location &&
      prevProps &&
      prevProps.location &&
      this.props.location.search !== prevProps.location.search
    )
      this.props.setSharedState('lastSearchParams', this.props.location.search);
  }

  openConsumptionInfo = () => {
    if (this.props.sharedState.isAccessoryConsumptionInfoClosed)
      this.props.setSharedState('isAccessoryConsumptionInfoClosed', false);
  };

  getGroupedCarlines(carlines: Array<Object>): Array<CarlineEntry> {
    // the model list shown as dropdown in EKI is based on the global `window.hwsconfig.carlines`.
    // `modelsFilter` is used as allow list to filter the list of carlines before rendering in the EKI.
    const modelsFilter = [
      'mazda2',
      'mazda2hybrid',
      'mazda3',
      'mazda5',
      'mazda6',
      'mazda6e',
      'mazdacx-3',
      'mazdacx-30',
      'mazdacx-60',
      'mazdacx-80',
      'mazdacx-5',
      'mazdamx-5',
      'mazdamx-30',
    ];
    const models = [];
    carlines
      // sort by model
      .sort((a, b) => {
        const seriesA = a.id.replace(/\D/g, '');
        const seriesB = b.id.replace(/\D/g, '');

        if (seriesA > seriesB) {
          return 1;
        } else if (seriesB > seriesA) {
          return -1;
        }

        return 0;
      })
      .forEach((carline) => {
        const entry = models.find((model) => model.id === carline.id);
        if (!entry) {
          models.push({
            id: carline.id,
            name: carline.model,
            years: [carline.startDate],
          });
        } else {
          entry.years.push(carline.startDate);
        }
      });

    const distinct = (value, index, self) => {
      return self.indexOf(value) === index;
    };

    models.forEach((model) => {
      model.years = model.years.filter(distinct);
    });

    return models.filter((carline) =>
      modelsFilter.includes(carline.name.toLowerCase().replace(/\s/g, ''))
    );
  }

  handleModelChange = (key: string) => {
    const currentModel = this.state.models.find((model) => model.id === key);

    if (currentModel) {
      this.props.history.push({ search: `?model=${currentModel.id}` });

      this.setState({
        currentModel: key,
        years: currentModel.years,
        currentYear: '',
        currentCategory: '',
        categories: [],
        parts: [],
      });
    }
  };

  handleYearChange = (year: string) => {
    this.props.history.push({ search: `?model=${this.state.currentModel}&year=${year}` });
    this.setState({ currentYear: year, currentCategory: '' }, this.loadCategories);
  };

  handleCategoryChange = (categoryKey: string) => {
    const { currentModel, currentYear } = this.state;
    const parts = this.state.categories[parseInt(categoryKey, 0)].parts;

    if (parts) {
      this.props.history.push({
        search: `?model=${currentModel}&year=${currentYear}&category=${categoryKey}`,
      });
      this.setState({ currentCategory: categoryKey, parts });
    }
  };

  loadCategories = () => {
    const { currentModel, currentYear, currentCategory } = this.state;
    const storeKey = `acc-${currentModel}-${currentYear}`;
    const cachedCategories = this.props.mainStore.get(storeKey);

    if (cachedCategories) {
      const parts = currentCategory ? cachedCategories[currentCategory].parts : [];
      return this.setState({ categories: cachedCategories, parts });
    }

    this.setState({ loading: true });

    return axios({
      method: 'get',
      url: '/api/v1/accessories/',
      params: {
        carline: currentModel,
        date: currentYear,
      },
    }).then((res) => {
      const categories = (res.data && res.data.categories) || [];
      let parts = [];

      if (categories.length > 0) {
        parts = currentCategory ? res.data.categories[currentCategory]?.parts : [];
        // cache categories in main store
        this.props.mainStore.set(storeKey, res.data.categories);
      }

      this.setState({ loading: false, parts, categories });
    });
  };

  renderPartList = () => {
    const { currentModel, currentYear, currentCategory, loading, categories, parts } = this.state;

    let hint;
    if (currentModel && currentYear && !categories.length) {
      hint =
        'Leider ist für das gewählte Model / Produktionsdatum aktuell kein Zubehör online verfügbar.';
    } else if (!String(currentYear).length) {
      hint = 'Bitte wählen Sie ein Produktionsdatum';
    } else if (!String(currentCategory).length) {
      hint = 'Bitte wählen Sie eine Kategorie';
    }

    const list =
      parts
        ?.filter((part) => !!part.includeInOAC)
        .sort((a, b) => a.accessoryName.localeCompare(b.accessoryName)) || [];

    return (
      <>
        <div role="alert" aria-live="assertive" className={styles.resultText}>
          {loading ? (
            <LoadingSpinner label="Ergebnisse laden" />
          ) : (
            <p>
              {hint ? hint : `${list.length} Ergebniss${list.length === 1 ? '' : 'e'} gefunden`}
            </p>
          )}
        </div>
        <ul className={styles.list}>
          {list.map((part) => (
            <li key={part.partNumber}>
              {
                <AccessoriesEntry
                  part={new AccessoryModel(part, this.props.dealer)}
                  model={currentModel}
                  year={currentYear}
                  dealer={this.props.dealer}
                />
              }
            </li>
          ))}
        </ul>
      </>
    );
  };

  render() {
    const { models, currentModel, years, currentYear, categories, currentCategory, loading } =
      this.state;

    const modelOptions = models
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((model) => {
        return {
          value: model.id,
          label: model.name,
        };
      });

    const yearOptions = years
      .sort((a, b) => {
        const date1 = new Date(a).getTime();
        const date2 = new Date(b).getTime();

        if (date1 < date2) {
          return 1;
        } else if (date1 > date2) {
          return -1;
        }

        return 0;
      })

      .map((year) => ({
        value: year,
        label: `Ab ${formatDate(new Date(year), 'MM/yyyy')}`,
      }));

    const categoryOptions = categories.map((category, i) => {
      return {
        value: i,
        label: category.name,
      };
    });

    return (
      <Fragment>
        <DefaultPage deeplinkId={this.props.deeplinkId}>
          <Container className={styles.accessoriesFilter}>
            <Row>
              <Col m={12}>
                <Typography.Headline
                  level="h3"
                  value="Wählen Sie Ihr Zubehör"
                  ariaLabel="Wählen Sie Ihren Mazda, das Produktionsdatum und die gewünschte Zubehörkategorie, um die verfügbaren Zubehörteile zu durchblättern"
                  forceUppercase={true}
                />
              </Col>
            </Row>
            <Row>
              <Col m={4}>
                <Dropdown
                  name="currentModel"
                  label="Modell wählen"
                  options={modelOptions}
                  value={currentModel}
                  onChange={this.handleModelChange}
                  onClick={this.openConsumptionInfo}
                />
              </Col>
              <Col m={4}>
                <Dropdown
                  name="currentYear"
                  label="Produktionsdatum wählen..."
                  placeholder="Produktionsdatum wählen..."
                  options={yearOptions}
                  value={currentYear}
                  onChange={this.handleYearChange}
                />
              </Col>
              <Col m={4}>
                <Dropdown
                  label="Kategorie wählen..."
                  placeholder="Kategorie wählen..."
                  name="currentCategory"
                  options={categoryOptions}
                  value={currentCategory}
                  onChange={this.handleCategoryChange}
                  disabled={loading || categoryOptions.length === 0}
                />
              </Col>
            </Row>
          </Container>
        </DefaultPage>
        <DefaultPage footerMargin topMargin>
          <Container>
            <div className={styles.partList}>{this.renderPartList()}</div>
          </Container>
        </DefaultPage>
      </Fragment>
    );
  }
}

export default withRouter(withUrlParams(AccessoriesList));
